"use client";

import { useCallback, useEffect, useState } from "react";
import { useConfirm } from "@/app/components/ConfirmDialog";
import BattleDemoBattle from "./BattleDemoBattle";
import BattleDemoShop from "./BattleDemoShop";
import { DetailBody, Modal } from "./BattleDemoParts";
import { BATTLE_DEMO_CSS } from "./battleDemoStyle";
import { BattleData, CharView, DemoResponse, DetailView, HistoryRun, Plan, RunSummary, callDemo } from "./battleDemoTypes";

type Screen = "top" | "battle" | "shop" | "history";

/** 戦闘デモプレイ。キャラを 1〜4 人選び、終わりの無い戦闘を進める。スマホの縦持ちを基準にする */
export default function BattleDemo({ onBack }: { onBack: () => void }) {
  const [screen, setScreen] = useState<Screen>("top");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [run, setRun] = useState<RunSummary | null>(null);
  const [characters, setCharacters] = useState<CharView[]>([]);
  const [history, setHistory] = useState<HistoryRun[]>([]);
  const [picks, setPicks] = useState<string[]>([]);
  const [battle, setBattle] = useState<BattleData | null>(null);
  const [shop, setShop] = useState<DemoResponse["shop"] | null>(null);
  const [detail, setDetail] = useState<DetailView | null>(null);
  const [ask, confirmDialog] = useConfirm();

  const request = useCallback(async (op: string, args?: unknown): Promise<DemoResponse | null> => {
    setBusy(true);
    setError("");
    try {
      const res = await callDemo(op, args);
      if (res.error) setError(res.error);
      if (res.run !== undefined) setRun(res.run);
      return res;
    } catch {
      setError("サーバーに繋がりません");
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(""), 4000);
    return () => clearTimeout(t);
  }, [error]);

  const loadTop = useCallback(async () => {
    const res = await request("state");
    if (!res) return;
    setCharacters(res.characters ?? []);
    setHistory(res.history ?? []);
    setBattle(null);
    setScreen("top");
  }, [request]);

  useEffect(() => {
    loadTop();
  }, [loadTop]);

  const enterBattle = useCallback(async () => {
    const res = await request("battle");
    if (!res || !res.battle) return;
    setBattle(res.battle);
    setScreen("battle");
  }, [request]);

  const enterShop = useCallback(async () => {
    const res = await request("shop");
    if (!res || !res.shop) return;
    setShop(res.shop);
    setScreen("shop");
  }, [request]);

  const resume = () => {
    if (!run) return;
    if (run.phase === "shop") enterShop();
    else enterBattle();
  };

  const start = async () => {
    if (run && !(await ask("進行中のデモを終えて、最初から始めます。", { ok: "始める" }))) return;
    const res = await request("start", { picks });
    if (!res || res.error) return;
    setPicks([]);
    await enterBattle();
  };

  const submitTurn = async (plans: Record<string, Plan>) => {
    const res = await request("turn", { plans });
    if (res && res.battle) setBattle(res.battle);
  };

  const abandon = async () => {
    if (!(await ask("このデモを終えて最初に戻ります。", { ok: "終える", danger: true }))) return;
    await request("abandon");
    await loadTop();
  };

  const togglePick = (key: string) => {
    setPicks((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : prev.length >= 4 ? prev : prev.concat(key)));
  };

  return (
    <div className="bd-root">
      <style>{BATTLE_DEMO_CSS}</style>
      {confirmDialog}
      {error && <div className="bd-error" onClick={() => setError("")}>{error}</div>}

      {screen === "top" && (
        <div className="bd-app bd-title">
          <header className="bd-title-head">
            <button className="bd-icon-btn bd-back" onClick={onBack}>戻る</button>
            {history.length > 0 && <button className="bd-icon-btn bd-hist" onClick={() => setScreen("history")}>履歴</button>}
            <div className="bd-title-sub">ラクエン ・ Baunheim</div>
            <h1>戦闘デモ</h1>
            <p className="bd-lead">1〜4 人を選ぶ。3 回に 1 回ボス、5 回ごとに購入・修理。敗北と逃亡で最初から。</p>
          </header>

          {run && (
            <section className="bd-resume">
              <p>進行中のデモが在る：<strong>{run.stage} 回目</strong>（{run.phase === "shop" ? "購入・修理フェーズ" : "戦闘"}）</p>
              <p className="bd-dim">{run.party.map((c) => c.name).join("・")}</p>
              <div className="bd-row">
                <button className="bd-btn bd-btn-primary" disabled={busy} onClick={resume}>続きから</button>
                <button className="bd-btn bd-btn-danger" disabled={busy} onClick={abandon}>終える</button>
              </div>
            </section>
          )}

          <div className="bd-char-grid">
            {characters.map((c) => {
              const key = c.key ?? c.id;
              const on = picks.includes(key);
              return (
                <div key={key} className={`bd-char ${on ? "bd-char-on" : ""}`} onClick={() => togglePick(key)} role="button">
                  {on && <span className="bd-char-num">{picks.indexOf(key) + 1}</span>}
                  <div className="bd-char-name">{c.name}</div>
                  <div className="bd-char-titleline">{c.title}{c.source === "master" ? "・master" : ""}</div>
                  <div className="bd-char-blurb">{c.blurb}</div>
                  <div className="bd-char-stats">
                    <span>HP {c.hp.max}</span>
                    {c.pools.map((p) => <span key={p.type}>{p.type.toUpperCase()} {p.max}</span>)}
                  </div>
                  {c.detail && (
                    <button className="bd-char-info" onClick={(e) => { e.stopPropagation(); setDetail(c.detail ?? null); }}>詳細</button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="bd-start-bar">
            <div className="bd-sel-line">{picks.length === 0 ? "仲間を選んで" : `${picks.length} 人`}</div>
            <button className="bd-btn bd-btn-primary" disabled={busy || picks.length < 1} onClick={start}>ゲームスタート</button>
          </div>
        </div>
      )}

      {screen === "history" && <HistoryScreen history={history} onBack={() => setScreen("top")} />}

      {screen === "battle" && battle && (
        <BattleDemoBattle
          data={battle}
          busy={busy}
          last={run?.last ?? null}
          over={run === null}
          onSubmit={submitTurn}
          onNext={() => (run && run.phase === "shop" ? enterShop() : enterBattle())}
          onTop={loadTop}
          onAbandon={abandon}
        />
      )}

      {screen === "shop" && shop && (
        <BattleDemoShop
          shop={shop}
          busy={busy}
          onAction={async (op, args) => {
            const res = await request(op, args);
            if (res && res.shop) setShop(res.shop);
          }}
          onLeave={async () => {
            const res = await request("leave_shop");
            if (res && !res.error) await enterBattle();
          }}
          onAbandon={abandon}
        />
      )}

      {detail && <Modal onClose={() => setDetail(null)}><DetailBody detail={detail} /></Modal>}
    </div>
  );
}

// ---- 履歴 ----

function HistoryScreen({ history, onBack }: { history: HistoryRun[]; onBack: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const [logText, setLogText] = useState<{ id: string; text: string } | null>(null);
  const resultLabel = (r: string) => (r === "defeat" ? "敗北" : r === "escape" ? "逃亡" : r === "abandon" ? "中断" : r === "victory" ? "勝利" : r);

  const showLog = async (id: string) => {
    const res = await callDemo("battle_log", { id });
    setLogText({ id, text: res.log ?? res.error ?? "" });
  };

  return (
    <div className="bd-app bd-page">
      <header className="bd-page-head">
        <h1>戦闘の履歴</h1>
        <button className="bd-icon-btn" onClick={onBack}>戻る</button>
      </header>
      {history.map((h) => (
        <section key={h.id} className="bd-card">
          <button className="bd-history-head" onClick={() => setOpen(open === h.id ? null : h.id)}>
            <span className="bd-dim">{new Date(h.started).toLocaleString("ja-JP")}</span>
            <span><strong>{h.wins} 勝</strong>・{h.stage} 回目で{resultLabel(h.result)}</span>
            <span className="bd-dim">{h.party.map((c) => c.name).join("・")}</span>
          </button>
          {open === h.id && (
            <div className="bd-history-battles">
              {h.battles.map((b) => (
                <button key={b.id} className="bd-history-battle" onClick={() => showLog(b.id)}>
                  <span>{b.stage} 回目{b.boss ? "（BOSS）" : ""}・{resultLabel(b.result)}・{b.turns} ターン</span>
                  <span className="bd-dim">{b.enemies.join("・")}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      ))}
      {logText && (
        <Modal onClose={() => setLogText(null)}>
          <div className="bd-m-title">戦闘ログ</div>
          <pre className="bd-pre">{logText.text}</pre>
        </Modal>
      )}
    </div>
  );
}
